/**
 * Shared tasks (Jens 2026-10-14): one task for several people, each with their own copy, linked by
 * `share_group_id`. Parents see and edit it as one task (server: supabase/functions/_shared/sharedTasks.ts).
 */
type T = {
  id: string;
  title?: string;
  assigned_to?: string | null;
  share_group_id?: string | null;
  is_completed?: boolean;
  verified?: boolean;
  pending_update?: any;
  created_at?: Date | string | null;
  checklist?: any[];
  tools?: string[];
};

const time = (t: T) => (t.created_at ? new Date(t.created_at as any).getTime() : 0);

/** Who a shared task is for: everyone with a copy, in the family's member order. */
export function groupMembers(tasks: T[], groupId: string, memberOrder: string[] = []): string[] {
  const ids = new Set(tasks.filter((t) => t.share_group_id === groupId && t.assigned_to).map((t) => t.assigned_to as string));
  const ordered = memberOrder.filter((id) => ids.has(id));
  return [...ordered, ...Array.from(ids).filter((id) => !ordered.includes(id))];
}

/**
 * The copy that stands for the whole task in the editor: the newest copy that already has the
 * latest edit (an untouched one), else the newest copy with an edit waiting on it.
 */
export function groupRepresentative<R extends T>(tasks: R[], groupId: string): R | null {
  const rows = tasks.filter((t) => t.share_group_id === groupId).sort((a, b) => time(b) - time(a));
  return rows.find((t) => !t.is_completed && !t.pending_update)
    ?? rows.find((t) => t.pending_update)
    ?? rows[0]
    ?? null;
}

/** What the editor should show: a waiting edit counts as the task's current content. */
export function withPendingEdit<R extends T>(task: R): R {
  const p = task.pending_update;
  if (!p) return task;
  const checklist = Array.isArray(p.checklist)
    ? p.checklist.map((s: any, i: number) => ({ id: `pending-${i}`, text: s.text, images: s.images || [], tool_ids: s.tool_ids || [] }))
    : task.checklist;
  return { ...task, ...(p.fields || {}), checklist, tools: Array.isArray(p.tools) ? p.tools : task.tools };
}

export type ActiveCard<R> = { kind: 'single'; task: R } | { kind: 'group'; groupId: string; task: R; copies: R[] };

/** Open tasks as cards: one per ordinary task, one per shared task (all its open copies inside). */
export function activeCards<R extends T>(open: R[], all: R[]): Array<ActiveCard<R>> {
  const cards: Array<ActiveCard<R>> = [];
  const seen = new Set<string>();
  for (const t of open) {
    if (!t.share_group_id) { cards.push({ kind: 'single', task: t }); continue; }
    if (seen.has(t.share_group_id)) continue;
    seen.add(t.share_group_id);
    const rep = groupRepresentative(all, t.share_group_id) ?? t;
    cards.push({ kind: 'group', groupId: t.share_group_id, task: rep, copies: open.filter((o) => o.share_group_id === t.share_group_id) });
  }
  return cards;
}
